import { Fragment, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "@/lib/utils";
import ScoutVisual from "./ScoutVisual";
import VersioningVisual from "./VersioningVisual";
import DataProductVisual from "./DataProductVisual";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TabDef {
  id: string;
  label: string;
  badge: string;
  title: string | readonly string[];
  description: string | readonly string[];
}

// ---------------------------------------------------------------------------
// Tab data
// ---------------------------------------------------------------------------

const TABS: TabDef[] = [
  {
    id: "scout",
    label: "Scout",
    badge: "WORKFLOW SETUP",
    title: "Turn operating knowledge into a reviewed workflow.",
    description: [
      "Scout maps the sources, owners, fields, checks, and missing evidence before the first run. It reuses existing workflow logic where it can, then shows what still needs review before anything updates a record.",
    ],
  },
  {
    id: "versioning",
    label: "Versioned Workflows",
    badge: "EXECUTION ENGINE",
    title: "Governance built in.",
    description: [
      "Every node in the workflow can be inspected before a record changes. Teams can review node output and see exactly which business logic produced each field.",
      "When logic changes, Lumient shows the diff between versions: what changed, which runs used it, and which records were affected.",
    ],
  },
  {
    id: "dataproduct",
    label: "Live Records",
    badge: "MAINTAINED RECORDS",
    title: "Keep the record current.",
    description: [
      "Live Records hold the current operational state produced by each workflow run. Teams can search, filter, aggregate, and query records without reopening the source evidence.",
      "Each record keeps evidence, history, and lineage attached, so teams and agents work from the same maintained state instead of reconstructing it from raw files.",
    ],
  },
];

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EASE = [0.25, 0.1, 0.25, 1] as const;
const CONTAINER = "max-w-7xl mx-auto px-6 lg:px-16";
const EYEBROW = "text-xs font-medium tracking-[0.2em] uppercase mb-5";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function renderVisual(id: string) {
  if (id === "scout") return <ScoutVisual />;
  if (id === "versioning") return <VersioningVisual />;
  return <DataProductVisual />;
}

function renderTitle(title: TabDef["title"]) {
  if (!Array.isArray(title)) return title;

  return title.map((line, index) => (
    <Fragment key={line}>
      {index > 0 && <br />}
      {line}
    </Fragment>
  ));
}

function renderDescription(description: TabDef["description"]) {
  const paragraphs = Array.isArray(description) ? description : [description];

  return paragraphs.map((paragraph) => (
    <p key={paragraph} className="text-[0.95rem] font-light leading-[1.65] text-muted-foreground">
      {paragraph}
    </p>
  ));
}

// ---------------------------------------------------------------------------
// FeatureTabs
// ---------------------------------------------------------------------------

const FeatureTabs = () => {
  const [activeTab, setActiveTab] = useState("scout");
  const activeData = TABS.find((t) => t.id === activeTab)!;

  return (
    <section className="bg-surface-subtle border-b border-border">
      <div className={`${CONTAINER} py-20 lg:py-28`}>

        {/* Section header */}
        <motion.div
          className="mb-10 lg:mb-12 text-center"
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <p className={`${EYEBROW} text-center`} style={{ color: "oklch(var(--primary-readable))" }}>PRODUCT SURFACES</p>
          <h2 className="font-serif font-semibold text-[2.25rem] leading-[1.05] tracking-[-0.02em] text-foreground mx-auto max-w-2xl">
            Make recurring operations executable.
          </h2>
          <p className="mt-5 text-[1.05rem] font-light leading-[1.65] text-muted-foreground mx-auto max-w-xl">
            Built for teams whose work depends on evidence, review, and repeatability.
          </p>
        </motion.div>

        {/* Tabs */}
        <TabsPrimitive.Root value={activeTab} onValueChange={setActiveTab}>

          {/* Tab triggers */}
          <div className="-mx-1 px-1 pb-1">
            <TabsPrimitive.List className="mx-auto grid w-full grid-cols-3 gap-0.5 rounded-full border border-border bg-surface-panel p-1 sm:w-fit">
              {TABS.map((tab) => (
                <TabsPrimitive.Trigger
                  key={tab.id}
                  value={tab.id}
                  className={cn(
                    "flex items-center justify-center gap-1 rounded-full px-2 py-2 text-center text-[clamp(0.72rem,2.7vw,0.875rem)] font-medium leading-tight transition-colors duration-200 sm:gap-2 sm:px-5 sm:text-sm",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[oklch(var(--primary)_/_0.4)]",
                    "data-[state=active]:bg-muted data-[state=active]:text-foreground",
                    "data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:text-foreground",
                  )}
                >
                  {tab.id === activeTab && (
                    <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                  )}
                  {tab.label}
                </TabsPrimitive.Trigger>
              ))}
            </TabsPrimitive.List>
          </div>

          {/* Panels */}
          {TABS.map((tab) => (
            <TabsPrimitive.Content
              key={tab.id}
              value={tab.id}
              forceMount
              className="mt-6 rounded-2xl border border-border bg-surface-panel overflow-hidden data-[state=inactive]:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[oklch(var(--primary)_/_0.4)]"
              style={{ boxShadow: "0 2px 12px oklch(0.12 0.008 75 / 0.08), inset 0 1px 0 oklch(1 0 0 / 0.5)" }}
            >
              {tab.id === activeTab ? (
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTab}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.45, ease: EASE }}
                    className="p-6 md:p-8 lg:p-10"
                  >
                    <div className="grid min-w-0 gap-10 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:items-center">

                  {/* Left: text */}
                  <div className="max-w-lg min-w-0">
                    <span
                      className="inline-block font-mono text-[0.62rem] tracking-[0.18em] uppercase mb-5 px-2.5 py-1 rounded border"
                      style={{
                        color: "oklch(var(--primary))",
                        borderColor: "oklch(var(--primary) / 0.35)",
                        background: "oklch(var(--primary) / 0.06)",
                      }}
                    >
                      {activeData.badge}
                    </span>

                    <h3 className="font-serif font-semibold text-[1.75rem] leading-[1.12] tracking-[-0.02em] text-foreground mb-5">
                      {renderTitle(activeData.title)}
                    </h3>

                    <div className="space-y-4">
                      {renderDescription(activeData.description)}
                    </div>
                  </div>

                  {/* Right: visual */}
                  <div className="min-w-0 overflow-hidden">
                    {renderVisual(activeTab)}
                  </div>

                </div>
                  </motion.div>
                </AnimatePresence>
              ) : null}
            </TabsPrimitive.Content>
          ))}

        </TabsPrimitive.Root>
      </div>
    </section>
  );
};

export default FeatureTabs;
